'use client';
import React from 'react'
import IconWrapper from '../IconWrapper/IconWrapper';
import { LanguageType, LanguageDirection } from '@/lib/types';

export type HorizontalTabItemSize = 14 | 16 | 20;

export interface HorizontalTabItemProps {
	text: string
	size: HorizontalTabItemSize
	language?: LanguageType
	children?: React.ReactElement<{ color?: string, size?: number, className?: string }>;
	isActive?: boolean;
	onClick?: () => void;
}

const LANGUAGE_TO_FLEX: Record<LanguageType, string> = {
	english: "flex-row",
	arabic: "flex-row-reverse"
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
}

const SIZE_TO_ICON_SIZE: Record<HorizontalTabItemSize, number> = {
	14: 16,
	16: 20,
	20: 24,
}

const SIZE_TO_TEXT_STYLE: Record<HorizontalTabItemSize, string> = {
	14: 'text-14-fixed--alexandria',
	16: 'text-16-fixed--alexandria',
	20: 'text-20-fixed--alexandria',
}

const SIZE_TO_PADDING_X: Record<HorizontalTabItemSize, string> = {
	14: '0.75rem',
	16: '0.75rem',
	20: '1rem',
}

const SIZE_TO_PADDING_Y: Record<HorizontalTabItemSize, string> = {
	14: '0.625rem',
	16: '0.625rem',
	20: '0.75rem',
}

const SIZE_TO_GAP: Record<HorizontalTabItemSize, string> = {
	14: '0.375rem', // 6px
	16: '0.5rem',   // 8px
	20: '0.5rem',   // 8px
}

const HorizontalTabItem: React.FC<HorizontalTabItemProps> = ({
	text,
	size,
	language = "english",
	isActive = false,
	children,
	onClick = () => { }
}) => {
	return (
		<div className={`
			w-full
			pb-[0.75rem]
			transition-colors duration-300 ease-in-out
			${isActive ? 'border-b-secondary border-theme-yellow-primary-base' : 'border-b-secondary border-stroke-soft-200'}
		`}>
			<button
				onClick={onClick}
				className={`
					w-full
					h-[52px]
					flex items-center justify-center
					group
					cursor-pointer
					disabled:cursor-not-allowed
					transition-[background-color, border] duration-300 ease-[ease]
				`}>
				<div
					style={{
						gap: SIZE_TO_GAP[size],
						paddingInlineStart: SIZE_TO_PADDING_X[size],
						paddingInlineEnd: SIZE_TO_PADDING_X[size],
						paddingTop: SIZE_TO_PADDING_Y[size],
						paddingBottom: SIZE_TO_PADDING_Y[size],
					}}
					className={`
						rounded-[12px]
						focus:outline-none focus:ring-0
						transition-colors duration-300 ease-[ease]
						border-main
						border-transparent hover:border-stroke-soft-200
						flex
						flex-wrap
						${LANGUAGE_TO_FLEX[language]}
						items-center justify-center
					`}>
					{children &&
						<IconWrapper
							size={SIZE_TO_ICON_SIZE[size]}
							className={`
								self-center

								${isActive ?
									`
								text-graphics-primary
								`
									:
									`
								text-graphics-secondary group-hover:text-graphics-tertiary
								`
								}
					`}
						>
							{children}
						</IconWrapper>
					}
					<p
						style={{
							direction: LANGUAGE_TO_DIRECTION[language],
						}}
						className={`
							whitespace-nowrap
							w-fit
							${isActive ?
								`
								text-text-primary
								`
								:
								`
								text-text-tertiary hover:text-text-quaternary
								`
							}
							${SIZE_TO_TEXT_STYLE[size]}
						`}
					>
						{text}
					</p>
				</div>
			</button >
		</div>
	);
}

export default HorizontalTabItem;
